import { Component, Inject, Input } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BASE_INDI_URL } from 'src/app/injection-tokens';
import { SKUValidator } from 'src/app/utils/sku-validator';
import { quantityRangeValidator } from '../manco-request-validators';
import { DeliveryStatus, Item, Orderline } from '../../order-details';
import { MancoOrderline } from '../../manco-order-request';
import { MancoOrderlineForm, RawMancoOrderlineWithIndex } from '../manco-form-types';

@Component({
  selector: 'app-new-manco-form-orderlines',
  templateUrl: './new-manco-form-orderlines.component.html',
  styleUrls: ['./new-manco-form-orderlines.component.scss'],
})
export class NewMancoFormOrderlinesComponent {
  // Customer-context for the ATP check (threaded down to app-atp-info).
  @Input() customerNumber?: string;
  @Input() businessCustomer: boolean = false;
  @Input() orderlines!: FormArray<FormGroup<MancoOrderlineForm>>;
  @Input() originalLength!: number;

  requiredValidator = Validators.required.bind(null);

  constructor(
    @Inject(BASE_INDI_URL) readonly baseIndiUrl: string,
    private fb: FormBuilder
  ) {}

  get hasSelectedOrderlines(): boolean {
    return this.orderlines.value.some(orderline => orderline.checked);
  }

  get allSelectedOrderlinesAreValid(): boolean {
    const hasInvalidOrderline = this.orderlines.controls.some(
      orderline => orderline.controls.checked.value && !orderline.valid
    );

    return !hasInvalidOrderline;
  }

  get valid(): boolean {
    return this.hasSelectedOrderlines && this.allSelectedOrderlinesAreValid;
  }

  get allOrderlinesSelected(): boolean {
    return (
      this.orderlines.controls.length > 0 && this.orderlines.controls.every(control => control.get('checked')?.value)
    );
  }

  asOrderline(formGroup: FormGroup<MancoOrderlineForm>): Orderline {
    const formGroupValue = formGroup.value;

    const item: Item = {
      sku: formGroupValue.sku ?? '',
      description: formGroupValue.name ?? '',
      units: formGroupValue.units ?? '',
    };

    const deliveryStatus: DeliveryStatus = formGroupValue.deliveryStatus ?? {
      deliveries: [],
      expectedDeliveries: [],
      quantityInProcess: 0,
    };

    return {
      item,
      quantityOrdered: formGroupValue.quantityOrdered ?? 0,
      quantityReturned: formGroupValue.quantityReturned ?? 0,
      quantityCancelled: formGroupValue.quantityCancelled ?? 0,
      stepQuantity: formGroupValue.stepQuantity ?? 1,
      deliveryStatus,
    };
  }

  createOriginalOrderlineGroup(orderline: Orderline, index: number): FormGroup {
    const group = this.fb.group<MancoOrderlineForm>({
      lineIDSource: new FormControl<number>(index + 1, { nonNullable: true }),
      checked: new FormControl<boolean>(false, { nonNullable: true }),
      sku: new FormControl<string>(orderline.item.sku, { nonNullable: true }),
      name: new FormControl<string>(orderline.item.description, { nonNullable: true }),
      orderlineRef: new FormControl<string>('', { nonNullable: true }),
      quantity: new FormControl<number>(
        { value: 0, disabled: true },
        {
          nonNullable: true,
          validators: [
            this.requiredValidator,
            quantityRangeValidator(orderline.quantityOrdered, orderline.quantityReturned),
          ],
        }
      ),
      quantityOrdered: new FormControl<number>(orderline.quantityOrdered),
      quantityReturned: new FormControl<number>(orderline.quantityReturned),
      units: new FormControl<string>(orderline.item.units, { nonNullable: true }),
      deliveryStatus: new FormControl<DeliveryStatus>(orderline.deliveryStatus, { nonNullable: true }),
      source: new FormControl<string>('original-order', { nonNullable: true }),
    });

    this.toggleQuantityAndSetFocus(group, index);

    return group;
  }

  createManualOrderlineGroup(): FormGroup<MancoOrderlineForm> {
    const newLineIDSource = this.orderlines.length + 1;
    const group = this.fb.group<MancoOrderlineForm>({
      lineIDSource: new FormControl<number>(newLineIDSource, { nonNullable: true }),
      checked: new FormControl<boolean>(true, { nonNullable: true }),
      sku: new FormControl<string>('', {
        nonNullable: true,
        validators: [this.requiredValidator, SKUValidator],
      }),
      name: new FormControl<string>('', { nonNullable: true }),
      orderlineRef: new FormControl<string>('', { nonNullable: true }),
      quantity: new FormControl<number>(0, {
        nonNullable: true,
        validators: [this.requiredValidator, Validators.pattern(/^(0\.\d{1,2}|[1-9]\d{0,3}(\.\d{1,2})?|10000)$/)],
      }),
      source: new FormControl<string>('manually-added', { nonNullable: true }),
    });

    this.toggleQuantityAndSetFocus(group, Number(newLineIDSource) - 1);

    return group;
  }

  toggleQuantityAndSetFocus(group: FormGroup<MancoOrderlineForm>, index: number): void {
    const checkMark = group.controls.checked;
    checkMark.valueChanges.subscribe((checked: boolean) => {
      const quantityControl = group.controls.quantity;
      if (!checked) {
        quantityControl.disable();
      } else {
        quantityControl.enable();
        this.setFocus(index);
      }
    });
  }

  addNewOrderline(): void {
    const newOrderlineGroup = this.createManualOrderlineGroup();
    newOrderlineGroup.markAllAsTouched();
    this.setFocus(this.orderlines.length);
    this.orderlines.push(newOrderlineGroup);
  }

  setFocus(index: number): void {
    setTimeout(() => {
      const input = document.getElementById(`quantity-input-${index.toString()}`);
      if (input) {
        input.focus();
      }
    });
  }

  reset(): void {
    while (this.orderlines.length) {
      this.orderlines.removeAt(0);
    }
  }

  selectAllOrderlines(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.orderlines.controls.forEach((formGroup: FormGroup<MancoOrderlineForm>) => {
      const controls: MancoOrderlineForm = formGroup.controls;
      controls.checked.setValue(isChecked);
      if (!controls.quantity.value && isChecked) {
        controls.quantity.setValue((controls.quantityOrdered?.value ?? 0) - (controls.quantityReturned?.value ?? 0));
      }
    });
  }

  getOrderlines(): MancoOrderline[] {
    return this.orderlines.controls
      .map((formGroup: FormGroup<MancoOrderlineForm>, index: number) => ({ formGroup, index }))
      .filter(({ formGroup }) => formGroup.controls.checked.value)
      .filter(({ formGroup }) => formGroup.controls.quantity.value != null)
      .map(({ formGroup, index }, newIndex: number) => ({
        lineIDSource: String(index + 1),
        rowNumber: newIndex + 1,
        sku: formGroup.controls.sku.value,
        nameSource:
          formGroup.controls.name.value && formGroup.controls.name.value.length > 0
            ? formGroup.controls.name.value
            : 'Handmatig toegevoegde regel',
        quantitySource: formGroup.controls.quantity.value ?? 0,
        orderlineRef: formGroup.controls.orderlineRef.value,
        properties: { source: formGroup.controls.source.value },
      }));
  }

  getOrderlinesOld(): MancoOrderline[] {
    return this.orderlines.controls
      .map<RawMancoOrderlineWithIndex>(
        (orderlineGroup: FormGroup<MancoOrderlineForm>, index: number) =>
          ({
            ...orderlineGroup.value,
            originalIndex: index,
          }) as unknown as RawMancoOrderlineWithIndex
      )
      .filter((orderline: RawMancoOrderlineWithIndex) => orderline.checked.value)
      .filter((orderline: RawMancoOrderlineWithIndex) => orderline.quantity.value !== null)
      .map<MancoOrderline>((orderline: RawMancoOrderlineWithIndex, newIndex: number) => ({
        lineIDSource: String(orderline.originalIndex + 1),
        rowNumber: newIndex + 1,
        sku: orderline.sku.value,
        nameSource: orderline.name.value ? orderline.name.value : 'Handmatig toegevoegde regel',
        quantitySource: orderline.quantity.value ?? 0, // This should not happen due to validation and filter
        orderlineRef: orderline.orderlineRef.value,
        properties: { source: orderline.source.value },
      }));
  }

  fillQuantity(orderline: FormGroup<MancoOrderlineForm>): void {
    const controls: MancoOrderlineForm = orderline.controls;
    if (
      controls.quantityOrdered?.value && //
      controls.quantity.value === 0 && //
      !controls.checked.value
    ) {
      controls.quantity.patchValue(controls.quantityOrdered.value - (controls.quantityReturned?.value ?? 0));
    }
  }

  toFormControl(control: AbstractControl | null): FormControl {
    return control as FormControl;
  }
}
